import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ReportCompanyHeader } from './reportHeader';

/** Plain numeric amount for PDF/report tables: negatives as (1,234.56), not -1,234.56 */
export const formatCurrency = (amount: number): string => {
  const numeric = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `(${numeric})` : numeric;
};

/** Extra vertical space (mm) after major total rows in financial statement PDFs (e.g. Total Assets, Total Liabilities). */
export const REPORT_PDF_GAP_AFTER_TOTAL_ROW_MM = 4;

interface AcroFormField {
  Rect?: [number, number, number, number];
  value?: string | string[];
  fontSize?: number;
  textAlign?: 'left' | 'center' | 'right';
  fontName?: string;
  fontStyle?: string;
  color?: string;
  fillColor?: number[] | string | null;
  BS?: { width: number };
  multiline?: boolean;
}

/**
 * Draws text on a PDF at baseline coordinates (default). When `editable: true`, creates an
 * AcroForm fillable field instead (legacy; reconciles baseline Y with AcroForm Rect).
 */
export const drawPdfText = (
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  options: {
    width?: number;
    height?: number;
    fontSize?: number;
    textAlign?: 'left' | 'center' | 'right';
    fontStyle?: 'normal' | 'bold' | 'italic' | 'bolditalic';
    maxWidth?: number;
    fontName?: string;
    multiline?: boolean;
    editable?: boolean;
    color?: string;
  } = {},
) => {
  const AcroFormTextField = (
    pdf as unknown as { AcroFormTextField: new () => AcroFormField }
  ).AcroFormTextField;

  const {
    fontSize = 9,
    textAlign = 'left',
    fontStyle = 'normal',
    fontName = 'helvetica',
    multiline = false,
    editable = true,
    color = '#000000',
  } = options;

  // Normalize font names to jsPDF internal registry names.
  // jsPDF's AcroForm createDefaultAppearanceStream calls getFont(field.fontName)
  // which looks up by these internal names, NOT by PDF standard aliases (Helv, Cour, etc.).
  // Using aliases causes a silent fallback to helvetica (Arial) during editing.
  let pdfFontName = fontName;
  const lowerFont = fontName.toLowerCase();
  if (lowerFont === 'helvetica' || lowerFont === 'arial') {
    pdfFontName = 'helvetica';
  } else if (lowerFont === 'courier') {
    pdfFontName = 'courier';
  } else if (lowerFont === 'times' || lowerFont === 'times-roman') {
    pdfFontName = 'times';
  } else if (lowerFont === 'notosans') {
    pdfFontName = 'NotoSans';
  }

  if (!AcroFormTextField || !editable) {
    // Use pdfFontName (normalized jsPDF internal name) so static text matches the
    // same font registration that AcroForm fields use for their DA stream.
    pdf.setFont(pdfFontName, fontStyle);
    pdf.setFontSize(fontSize);
    pdf.setTextColor(color && color.startsWith('#') ? color : '#000000');
    pdf.text(text, x, y, { align: textAlign, maxWidth: options.maxWidth });
    return;
  }

  // Set font for width calculation — must match pdfFontName so getTextWidth is accurate.
  pdf.setFont(pdfFontName, fontStyle);
  pdf.setFontSize(fontSize);

  const textWidth =
    options.width ||
    (options.maxWidth ? options.maxWidth : pdf.getTextWidth(text));

  // Calculate height based on lines if multiline
  let heightMm = options.height;
  if (!heightMm) {
    if (multiline && options.maxWidth) {
      const lines = pdf.splitTextToSize(text, options.maxWidth) as string[];
      heightMm = lines.length * fontSize * 0.3527 * 1.35;
    } else {
      heightMm = fontSize * 0.3527 * 1.35;
    }
  }

  const field = new AcroFormTextField();

  let rectX = x;
  if (textAlign === 'center') rectX = x - textWidth / 2;
  else if (textAlign === 'right') rectX = x - textWidth;

  // y is baseline. Rect needs top.
  const topMm = y - fontSize * 0.3527 * 1.05;

  field.Rect = [rectX - 1, topMm, textWidth + 2, heightMm];
  field.value = text;
  field.fontSize = fontSize;
  field.textAlign = textAlign;
  field.fontName = pdfFontName;
  field.fontStyle = fontStyle;
  field.color = color;
  field.fillColor = [255, 255, 255, 0]; // Transparent background (RGBA 0 alpha)
  Object.assign(field, { BS: { width: 0 } }); // Border style 0 to eliminate blue highlights/borders
  if (multiline) field.multiline = true;

  (pdf as unknown as { addField: (field: AcroFormField) => void }).addField(
    field,
  );
};

export const calculateTextHeight = (
  pdf: jsPDF,
  text: string,
  maxWidth: number,
  fontSize: number,
): number => {
  const lines = pdf.splitTextToSize(text, maxWidth) as string[];
  return Math.max(5, lines.length * (fontSize * 0.3527 * 1.35));
};

// Create a print container with standard styling
export const createPrintContainer = (): HTMLElement => {
  const printContainer = document.createElement('div');
  printContainer.style.position = 'absolute';
  printContainer.style.left = '-9999px';
  printContainer.style.width = '210mm'; // A4 width
  printContainer.style.backgroundColor = 'white';
  // Standard margins for reports
  printContainer.style.padding = '0 15mm';
  printContainer.style.boxSizing = 'border-box';
  printContainer.style.fontFamily = 'Arial, sans-serif';
  printContainer.style.color = '#000000';
  return printContainer;
};

// Create a standard report header
export const createReportHeader = (
  reportTitle: string,
  periodText: string,
  companyHeader?: ReportCompanyHeader,
): HTMLElement => {
  const header = document.createElement('div');
  header.style.textAlign = 'center';
  header.style.marginBottom = '20px';
  header.style.marginTop = '0';

  if (companyHeader) {
    const businessName = document.createElement('div');
    businessName.textContent = companyHeader.registeredBusinessName;
    businessName.style.fontSize = '16px';
    businessName.style.fontWeight = 'normal';
    businessName.style.color = '#000000';
    businessName.style.marginBottom = '2px';
    businessName.style.letterSpacing = 'normal';

    const businessAddress = document.createElement('div');
    businessAddress.textContent = companyHeader.registeredBusinessAddress;
    businessAddress.style.fontSize = '13px';
    businessAddress.style.color = '#000000';
    businessAddress.style.marginBottom = '2px';
    businessAddress.style.letterSpacing = 'normal';

    const businessTin = document.createElement('div');
    businessTin.textContent = `TIN: ${companyHeader.registeredBusinessTin}`;
    businessTin.style.fontSize = '13px';
    businessTin.style.color = '#000000';
    businessTin.style.marginBottom = '6px';
    businessTin.style.letterSpacing = 'normal';

    header.appendChild(businessName);
    header.appendChild(businessAddress);
    header.appendChild(businessTin);
  }

  const title = document.createElement('div');
  title.textContent = reportTitle;
  title.style.fontSize = '22px';
  title.style.fontWeight = 'bold';
  title.style.color = '#000000';
  title.style.marginBottom = '4px';
  title.style.letterSpacing = 'normal';

  const period = document.createElement('div');
  period.textContent = periodText;
  period.style.fontSize = '14px';
  period.style.color = '#000000';
  period.style.letterSpacing = 'normal';

  header.appendChild(title);
  header.appendChild(period);

  return header;
};

// Generate PDF from HTML container with proper margins and page breaks
export const generatePDFFromContainer = async (
  printContainer: HTMLElement,
  filename: string,
  reportTitle: string,
  returnPDF: boolean = false,
): Promise<jsPDF | void> => {
  document.body.appendChild(printContainer);

  // Generate canvas from HTML
  const canvas = await html2canvas(printContainer, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  // Remove temporary container
  document.body.removeChild(printContainer);

  // Calculate PDF dimensions with margins
  const pageWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const marginTop = 15; // Top margin in mm
  const marginBottom = 15; // Bottom margin in mm (increased for footer)
  const footerHeight = 10; // Space for footer text
  const usableHeight = pageHeight - marginTop - marginBottom;

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  // Calculate total number of pages first
  let tempSourceY = 0;
  let totalPages = 0;
  while (tempSourceY < canvas.height) {
    const remainingPixels = canvas.height - tempSourceY;
    const remainingHeight =
      imgHeight - (tempSourceY / canvas.height) * imgHeight;
    const heightOnPage = Math.min(remainingHeight, usableHeight);
    const sourceHeight = Math.min(
      remainingPixels,
      (heightOnPage / imgHeight) * canvas.height,
    );
    tempSourceY += sourceHeight;
    totalPages++;
  }

  // Create PDF
  const pdf = new jsPDF('p', 'mm', 'a4');

  let sourceY = 0; // Position in pixels in the source canvas
  let pageNumber = 0;

  // Split content across pages with proper margins
  while (sourceY < canvas.height) {
    if (pageNumber > 0) {
      pdf.addPage();
    }

    // Calculate how much of the canvas fits on this page
    const remainingPixels = canvas.height - sourceY;
    const remainingHeight = imgHeight - (sourceY / canvas.height) * imgHeight;
    const heightOnPage = Math.min(remainingHeight, usableHeight);

    // Calculate the source rectangle and destination
    // Convert mm height to pixel height: sourceHeight / canvas.height = heightOnPage / imgHeight
    const sourceHeight = Math.min(
      remainingPixels,
      (heightOnPage / imgHeight) * canvas.height,
    );
    const destY = marginTop;

    // Create a temporary canvas for this page's content
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourceHeight;
    const pageCtx = pageCanvas.getContext('2d');

    if (pageCtx) {
      // Draw the portion of the image for this page
      pageCtx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sourceHeight, // Source rectangle
        0,
        0,
        canvas.width,
        sourceHeight, // Destination rectangle
      );

      const pageImgData = pageCanvas.toDataURL('image/png');
      const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;

      pdf.addImage(pageImgData, 'PNG', 0, destY, imgWidth, pageImgHeight);
    }

    // Add footer with title and page number on the right
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    const footerY = pageHeight - marginBottom + footerHeight;

    // Combined text: "Report Title — Page X of Y" on the right
    const footerText = `${reportTitle} — Page ${pageNumber + 1} of ${totalPages}`;
    const footerTextWidth = pdf.getTextWidth(footerText);
    pdf.text(footerText, pageWidth - 10 - footerTextWidth, footerY);

    // Increment sourceY by the pixel height we just processed
    sourceY += sourceHeight;
    pageNumber++;
  }

  // Download PDF or return it
  if (returnPDF) {
    return pdf;
  }
  pdf.save(filename);
};

// Convert HTML container to images (reuses PDF generation logic but outputs images)
export const convertPDFToImages = async (
  printContainer: HTMLElement,
  filename: string,
  reportTitle: string,
): Promise<void> => {
  document.body.appendChild(printContainer);

  // Generate canvas from HTML (same as PDF generation)
  const canvas = await html2canvas(printContainer, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: 1200,
    windowHeight: printContainer.scrollHeight + 200,
  });

  // Remove temporary container
  document.body.removeChild(printContainer);

  // A4 dimensions in mm
  const pageWidthMm = 210; // A4 width in mm
  const pageHeightMm = 297; // A4 height in mm
  const marginTopMm = 15; // Top margin in mm
  const marginBottomMm = 15; // Bottom margin in mm

  // Convert mm to pixels at 150 DPI (good balance for web/print)
  // 1 inch = 25.4mm, 150 DPI = 150 pixels per inch
  const mmToPx = 300 / 25.4; // pixels per mm at 300 DPI for high quality
  const pageWidthPx = Math.round(pageWidthMm * mmToPx); // A4 width in pixels
  const pageHeightPx = Math.round(pageHeightMm * mmToPx); // A4 height in pixels
  const marginTopPx = Math.round(marginTopMm * mmToPx);
  const marginBottomPx = Math.round(marginBottomMm * mmToPx);
  const usableHeightPx = pageHeightPx - marginTopPx - marginBottomPx;

  // Calculate the scale factor to fit the canvas content to A4 width
  const scaleFactor = pageWidthPx / canvas.width;
  const scaledContentHeight = canvas.height * scaleFactor;

  // Calculate total number of pages first
  let tempSourceY = 0;
  let totalPages = 0;
  while (tempSourceY < scaledContentHeight) {
    const remainingPixels = scaledContentHeight - tempSourceY;
    const heightOnPage = Math.min(remainingPixels, usableHeightPx);
    tempSourceY += heightOnPage;
    totalPages++;
  }

  let sourceY = 0; // Position in scaled pixels
  let pageNumber = 0;

  // Split content across pages and generate A4-sized images
  while (sourceY < scaledContentHeight) {
    // Calculate how much content fits on this page
    const remainingPixels = scaledContentHeight - sourceY;
    const heightOnPage = Math.min(remainingPixels, usableHeightPx);

    // Calculate source rectangle in original canvas coordinates
    const sourceYOriginal = sourceY / scaleFactor;
    const sourceHeightOriginal = heightOnPage / scaleFactor;

    // Create an A4-sized canvas for this page
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = pageWidthPx;
    pageCanvas.height = pageHeightPx;
    const pageCtx = pageCanvas.getContext('2d');

    if (!pageCtx) {
      throw new Error('Failed to create page canvas context');
    }

    // Fill white background
    pageCtx.fillStyle = '#ffffff';
    pageCtx.fillRect(0, 0, pageWidthPx, pageHeightPx);

    // Draw the portion of the image for this page, scaled to A4 width
    pageCtx.drawImage(
      canvas,
      0,
      sourceYOriginal,
      canvas.width,
      sourceHeightOriginal, // Source rectangle in original canvas
      0, // Start drawing at left edge
      marginTopPx, // Start drawing at top margin
      pageWidthPx, // Full A4 width
      heightOnPage, // Scaled height
    );

    // Draw footer (same format as PDF)
    const footerText = `${reportTitle} — Page ${pageNumber + 1} of ${totalPages}`;
    pageCtx.fillStyle = '#000000';
    pageCtx.font = '12px Arial';
    pageCtx.textAlign = 'right';
    pageCtx.textBaseline = 'middle';
    const footerY = pageHeightPx - marginBottomPx / 2; // Center footer in bottom margin
    pageCtx.fillText(footerText, pageWidthPx - 20, footerY);

    // Convert to JPG and download
    const imageData = pageCanvas.toDataURL('image/jpeg', 0.95);
    const link = document.createElement('a');
    link.download = `${filename.replace('.pdf', '')}_page_${pageNumber + 1}.jpg`;
    link.href = imageData;

    // Small delay for multiple pages to help the browser handle multiple downloads
    if (pageNumber > 0) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    link.click();

    // Increment sourceY by the scaled height we just processed
    sourceY += heightOnPage;
    pageNumber++;
  }
};

// Helper function to create a row with label and amount
export const createAmountRow = (
  label: string,
  amount: number,
  options?: {
    fontWeight?: string;
    fontSize?: string;
    borderTop?: string;
    marginTop?: string;
    paddingTop?: string;
  },
): HTMLElement => {
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.justifyContent = 'space-between';
  row.style.paddingTop = options?.paddingTop || '4px';
  row.style.paddingBottom = '4px';
  row.style.fontSize = options?.fontSize || '14px';
  row.style.lineHeight = 'normal';
  if (options?.fontWeight) row.style.fontWeight = options.fontWeight;
  if (options?.borderTop) row.style.borderTop = options.borderTop;
  if (options?.marginTop) row.style.marginTop = options.marginTop;

  const labelSpan = document.createElement('span');
  labelSpan.textContent = label;
  labelSpan.style.textAlign = 'left';
  labelSpan.style.flex = '1';
  labelSpan.style.letterSpacing = 'normal';

  const amountSpan = document.createElement('span');
  amountSpan.textContent = formatCurrency(amount);
  amountSpan.style.textAlign = 'right';
  amountSpan.style.fontFamily = 'monospace';
  amountSpan.style.minWidth = '120px';
  amountSpan.style.letterSpacing = 'normal';
  if (options?.fontWeight) amountSpan.style.fontWeight = options.fontWeight;

  row.appendChild(labelSpan);
  row.appendChild(amountSpan);

  return row;
};

// Helper function to create a section header
export const createSectionHeader = (title: string): HTMLElement => {
  const header = document.createElement('div');
  header.style.marginBottom = '10px';
  header.style.fontSize = '14px';
  header.style.fontWeight = '600';
  header.style.letterSpacing = 'normal';
  header.textContent = title;
  return header;
};

// Helper function to create a double underline
export const createDoubleUnderline = (): HTMLElement => {
  const underline = document.createElement('div');
  underline.style.borderBottom = '2px solid #000';
  underline.style.marginTop = '2px';
  return underline;
};
